import { useMotionConfig, useTheme } from '@nivo/core';
import { Funnel } from '@nivo/funnel';
import React from 'react';
import { animated, useSpring } from 'react-spring';
import InformationOutlineIcon from '../../assets/InformationOutlineIcon';
import useIsBrowser from '../../hooks/useIsBrowser';

/**
 * Will format a number to the "xk/M" format
 * x -> the number
 * k -> thousand
 * M -> million
 * @param number
 */
function formatThousandNumbers(number: number | string): string {
  try {
    if (typeof number === 'string') {
      return number;
    }

    const intNumber = parseInt(
      number?.toString()?.replace(/,/g, '')?.replace(/\./g, '') || ''
    );
    if (isNaN(intNumber)) {
      return '';
    }
    if (intNumber < 1) {
      return '0';
    } else if (intNumber < 100) {
      return '0.1k';
    } else if (intNumber < 10000) {
      return `${(intNumber / 1000).toFixed(1)}k`;
    } else if (intNumber < 1000000) {
      return `${(intNumber / 1000).toFixed(0)}k`;
    } else if (intNumber < 10000000) {
      return `${(intNumber / 1000000).toFixed(1)}M`;
    } else {
      return `${(intNumber / 1000000).toFixed(0)}M`;
    }
  } catch {
    return '';
  }
}

// eslint-disable-next-line
export const PartLabel = ({ part }: any) => {
  const theme = useTheme();
  const { animate, config: motionConfig } = useMotionConfig();

  const isLightColor = (colorString: string) => {
    // Extract RGB values from the color string
    const [, r, g, b] = colorString.match(/\d+/g) || [];
    const luminance =
      (0.299 * parseInt(r) + 0.587 * parseInt(g) + 0.114 * parseInt(b)) / 255;

    return luminance > 0.55;
  };

  const labelColor = isLightColor(part.color) ? 'black' : 'white';

  const animatedProps = useSpring({
    transform: `translate(${part.x}, ${part.y})`,
    color: labelColor,
    config: motionConfig,
    immediate: !animate,
    fontSize: '10px',
  });

  return (
    <animated.g transform={animatedProps.transform}>
      <animated.text
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          ...theme.labels.text,
          fill: animatedProps.color,
          pointerEvents: 'none',
          fontSize: animatedProps.fontSize,
        }}
      >
        {part.data.label}
      </animated.text>
    </animated.g>
  );
};

// eslint-disable-next-line
function Labels(props: any) {
  return props.parts.map(
    (part: { data: { id: string; color: string; x: string; y: string } }) => (
      <PartLabel key={part.data.id} part={part} />
    )
  );
}

// We need this tooltip to change the default one which has a square dot
// eslint-disable-next-line
const CustomTooltip = (part: any) => (
  <div className="flex items-center gap-2 rounded-xl bg-white p-3 text-xs shadow-[0px_0px_8px_-1px_rgba(0,0,0,0.08)]">
    <div
      style={{ backgroundColor: `${part.color}` }}
      className={`size-2 rounded-full `}
    />
    <span>{part.data.label}: </span>
    <span className="font-bold">{part.formattedValue}</span>
  </div>
);

// eslint-disable-next-line
export default function FunnelDiagram({ data, title, description }: any) {
  const isBrowser = useIsBrowser();

  if (!isBrowser) {
    return null;
  }

  if (!data?.length) {
    return null;
  }

  return (
    <div className="flex shrink grow basis-0 flex-col gap-6 rounded-xl bg-white p-6 shadow-[0px_2px_16px_-4px_rgba(9,9,11,0.08)]">
      <div className="flex items-center gap-1">
        <h6 className="text-lg font-semibold">{title}</h6>
        <InformationOutlineIcon fill="#A1A1AA" width={16} height={16} />
      </div>
      <div className="h-[400px] min-h-[400px] max-w-[400px] place-self-center">
        <Funnel
          height={400}
          width={400}
          borderWidth={1}
          interpolation="linear"
          data={data}
          enableAfterSeparators={false}
          enableBeforeSeparators={false}
          shapeBlending={0}
          valueFormat=">-.4s"
          layers={['separators', 'parts', Labels, 'annotations']}
          colors={generateColorPalette(data)}
          labelColor={{ from: 'black', modifiers: [['darker', -5]] }}
          tooltip={({ part }) => CustomTooltip(part)}
        />
      </div>
      <div className="inline-flex h-4 items-center justify-start gap-2.5">
        <div className="flex h-4 items-center justify-start gap-2">
          <div className="relative justify-start text-xs font-normal leading-none text-zinc-600">
            {formatThousandNumbers(
              Math.min(
                ...data.map(({ value }: { value: number }) => {
                  return value;
                })
              )
            )}
          </div>
          <div className="relative h-1.5 w-[116px] bg-gradient-to-r from-[#d4f3e9] to-[#226357]" />
          <div className="relative justify-start text-xs font-normal leading-none text-zinc-600">
            {formatThousandNumbers(
              Math.max(
                ...data.map(({ value }: { value: number }) => {
                  return value;
                })
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function generateColorPalette(data: { value: number }[]) {
  const baseColor = [0, 98, 80]; // RGBA components
  const alpha = 1;

  const max = Math.max(...data.map((item) => item.value));
  const min = Math.min(...data.map((item) => item.value));

  return data.map((item) => {
    const percentage = 1 - (item.value - min) / (max - min); // Adjusted percentage
    const newColor = baseColor.map((component) =>
      Math.round(
        component +
          (!Number.isNaN(percentage) ? percentage : 0) * (255 - component - 20)
      )
    );

    return `rgba(${newColor.join(', ')}, ${alpha})`;
  });
}
